import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';

export interface NotificationSettings {
  emailNotificationsEnabled: boolean;
  coursePublishedEmailEnabled: boolean;
  leaderboardDropEmailEnabled: boolean;
  testReminderEmailEnabled: boolean;
}

export type NotificationSettingsUpdate = Partial<NotificationSettings>;

interface CreateNotificationParams {
  title: string;
  text: string;
  isOrgWide: boolean;
  departmentIds?: number[];
  organisationId: number;
}

async function fetchNotificationSettings(
  organisationId: number,
): Promise<NotificationSettings> {
  const response = await apiFetch(
    ENDPOINTS.NOTIFICATION_SETTINGS(organisationId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || 'Failed to fetch notification settings',
    );
  }

  return response.json();
}

export function useNotificationSettings(organisationId: number) {
  return useQuery({
    queryKey: ['notification', 'settings', organisationId],
    queryFn: () => fetchNotificationSettings(organisationId),
  });
}

export function useUpdateNotificationSettings(organisationId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (update: NotificationSettingsUpdate) => {
      const response = await apiFetch(
        ENDPOINTS.NOTIFICATION_SETTINGS(organisationId),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Failed to update notification settings',
        );
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notification', 'settings', organisationId],
      });
    },
  });
}

export function useCreateNotification() {
  return useMutation({
    mutationFn: async (params: CreateNotificationParams) => {
      const response = await apiFetch(ENDPOINTS.NOTIFICATION_CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create notification');
      }

      return response.json();
    },
  });
}
